import React, { useContext, useState, useMemo } from 'react';
import { Button, Grid } from '@material-ui/core';
import ContentHeader from 'components/ContentHeader';
import ContentWrapper from 'components/ContentWrapper';
import BrowseData from 'components/BrowseData';
import BrowseProvider from 'providers/browse';
import { FilterValues } from 'types/browse';
import useStyles from '../styles';
import { Formik } from 'formik';
import SelectWithData from './SelectWithData';
import Input from './Input';
import BrowseContext from 'contexts/browseContext';
import NumberFormat from 'react-number-format';
import { useDispatch } from 'store';
import { addFeedback } from 'actions';
import { axiosInstance } from 'App';
import { saveAs } from 'file-saver';
import { Backdrop, CircularProgress } from '@material-ui/core';
import { useAuth } from 'providers/auth';

import { useHistory, useLocation } from 'react-router-dom';

const link = '/quotes';

const browseConfig = {
    dataId: 'quotes',
    apiBasePath: link,
    createApiPath: (path: string, pageNum: number, pageSize: number, filterValues?: any) => {
        const urlParams = new URLSearchParams(window.location.search);
        const params = Object.fromEntries(urlParams.entries());
       
         if (Object.keys(params).length) {
            var esc = encodeURIComponent;

            const filters = Object.keys(params)
                .map((k) => {
                    return params[k] && esc(k) + '=' + esc(params[k]) + '&';
                })
                .join('');

            return `${path}?pageNum=${pageNum}&pageSize=${pageSize}&${filters}`;
        }
        return `${path}?pageNum=${pageNum}&pageSize=${pageSize}&QuoteStateID=1`;
    },
    tableStructure: [
        {
            title: '',
            align: 'left',
            actions: [
                { title: 'Edit Item/Revision', idField: ['quoteID', 'revision', 'itemID'], action: 'edit', path: link },
                { title: 'Create Job', idField: ['quoteID', 'revision', 'itemID'], action: 'new', path: '/jobs' },
            ],
            sticky: true,
        },
        {
            title: 'Quote No',
            fieldName: 'quoteID',
            showFilter: false,
            showSort: false,
            formatData: (item: any) => `${item['quoteID']}${item['revision']}${item['itemID']}`,
        },
        {
            title: 'Quote Date',
            fieldName: 'createdAt',
            showFilter: false,
            showSort: false,
            formatData: (item: any, fieldName: any) => {
                const d = item[fieldName];
                return `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;
            },
        },
        {
            title: 'Award Date',
            fieldName: 'awardedAt',
            showFilter: false,
            showSort: false,
            formatData: (item: any, fieldName: any) => {
                const d = item[fieldName];
                return `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;
            },
        },
        {
            title: 'Client',
            fieldName: 'clientName',
            showFilter: false,
            showSort: false,
            formatData: (item: any) => item.clientDetails?.name,
        },
        {
            title: 'Contact',
            fieldName: 'contactName',
            showFilter: false,
            showSort: false,
            formatData: (item: any) => item.clientDetails?.contactDetails?.name,
        },
        {
            title: 'Sales Rep',
            fieldName: 'salesRepName',
            showFilter: false,
            showSort: false,
        },
        {
            title: 'Selected By',
            fieldName: 'selectedByName',
            showFilter: false,
            showSort: false,
        },
        {
            title: 'Product Category',
            fieldName: 'productCategoryName',
            showFilter: false,
            showSort: false,
        },
        {
            title: 'Project',
            fieldName: 'project',
            showFilter: false,
            showSort: false,
        },
        {
            title: 'Product Area',
            fieldName: 'productAreaName',
            showFilter: false,
            showSort: false,
        },
        {
            title: 'Enquiry Source',
            fieldName: 'enquirySourceName',
            showFilter: false,
            showSort: false,
        },
        {
            title: 'Quote Status',
            fieldName: 'quoteStatus',
            showFilter: false,
            showSort: false,
        },
        {
            title: 'Combined Price',
            fieldName: 'combinedPrice',
            showFilter: false,
            showSort: false,
            formatData: (item: any, fieldName: any) => (
                <NumberFormat
                    value={item[fieldName]}
                    displayType={'text'}
                    thousandSeparator
                    isNumericString
                    prefix={'$'}
                    decimalScale={2}
                    fixedDecimalScale
                />
            ),
        },
        {
            title: 'Comments',
            fieldName: 'comments',
            showFilter: false,
            showSort: false,
        },
        {
            title: 'Fan Serial',
            fieldName: 'fanSerial',
            showFilter: false,
            showSort: false,
        },
        {
            title: 'Fan Design',
            fieldName: 'fanDesign',
            showFilter: false,
            showSort: false,
            formatData: (item: any) => item.attributes?.fanDesign,
        },
        {
            title: 'Fan Size',
            fieldName: 'fanSize',
            showFilter: false,
            showSort: false,
            formatData: (item: any) => item.attributes?.fanSize,
        },
        {
            title: 'Aura Job File No',
            fieldName: 'auraJobFileNo',
            showFilter: false,
            showSort: false,
            formatData: (item: any) => item.attributes?.auraJobFileNo,
        },
        {
            title: 'Industry',
            fieldName: 'industryName',
            showFilter: false,
            showSort: false,
        },
        {
            title: 'Application',
            fieldName: 'applicationName',
            showFilter: false,
            showSort: false,
        },
    ],
    urlPath: link,
    idField: 'quoteID',
};

const createFilterValues = (values:any , history: any, searchParams: any) => {
    const { QuoteNo, ...rest } = values;
    const QuoteNoSplit = QuoteNo.match(/[a-z]+|\d+/gi) || [];

    const filters = {
        ...rest,
        ...(QuoteNoSplit.length >= 1 && { QuoteID: QuoteNoSplit[0] }),
        ...(QuoteNoSplit.length >= 2 && { Revision: QuoteNoSplit[1] }),
        ...(QuoteNoSplit.length >= 3 && { ItemID: QuoteNoSplit[2] }),
        ...(rest['Created%2eMin'] !== '' && { 'Created%2eMin': new Date(rest['Created%2eMin'] + "T00:00:00.000+10:00").toISOString() }),
        ...(rest['Created%2eMax'] !== '' && { 'Created%2eMax': new Date(rest['Created%2eMax'] + "T24:00:00.000+10:00").toISOString() }),
        ...(rest['Awarded%2eMin'] !== '' && { 'Awarded%2eMin': new Date(rest['Awarded%2eMin'] + "T00:00:00.000+10:00").toISOString() }),
        ...(rest['Awarded%2eMax'] !== '' && { 'Awarded%2eMax': new Date(rest['Awarded%2eMax'] + "T24:00:00.000+10:00").toISOString() }),
        ...(rest['Attributes%2eExportTimestamp%2eMin'] !== '' && { 'Attributes%2eExportTimestamp%2eMin': new Date(rest['Attributes%2eExportTimestamp%2eMin'] + "T00:00:00.000+10:00").toISOString() }),
        ...(rest['Attributes%2eExportTimestamp%2eMax'] !== '' && { 'Attributes%2eExportTimestamp%2eMax': new Date(rest['Attributes%2eExportTimestamp%2eMax'] + "T24:00:00.000+10:00").toISOString() }),
    }

    searchParams = new URLSearchParams('');

    Object.keys(filters)
        .map((k) => {
            if (filters[k]){
                searchParams.set(k, filters[k]);
            }
        })

    history.replace({ search: searchParams.toString() });
        
    return filters;
};

const quoteStateList = [
    {
        "label": "Open",
        "id": 1
    },
    {
        "label": "Archived",
        "id": 2
    }
];

const probabiltyList = [        
    {
        label: '0%',
        id: '0'
    },
    {
        label: '10%',
        id: '10'
    },
    {
        label: '50%',
        id: '50'
    },
    {
        label: '75%',
        id: '75'
    },
    {
        label: '90%',
        id: '90'
    },
    {
        label: '100%',
        id: '100'
    }
];

const Quotes: React.FC = ({ isLoading, clientList, salesRepList, productAreaList, quoteStatusList, enquirySourceList, industryList, applicationList }: any) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { userAttr }: any = useAuth();

    const history = useHistory();
    const { search } = useLocation();
    const searchParams = useMemo(() => new URLSearchParams(search), [search]);

    const { setFilterValues, clearFilter } = useContext(BrowseContext);

    const [cReset, setCReset] = useState(`c${Date.now()}`);
    const [sReset, setSReset] = useState(`s${Date.now()}`);
    const [sBReset, setSBReset] = useState(`sB${Date.now()}`);
    const [paReset, setPaReset] = useState(`pa${Date.now()}`);
    const [qSReset, setQsReset] = useState(`qs${Date.now()}`);
    const [esReset, setEsReset] = useState(`es${Date.now()}`);
    const [iReset, setIReset] = useState(`i${Date.now()}`);
    const [aReset, setAReset] = useState(`a${Date.now()}`);
    const [qStateReset, setQStateReset] = useState(`qState${Date.now()}`);
    const [probStateReset, setProbStateReset] = useState(`prob${Date.now()}`);

    const params = Object.fromEntries(searchParams.entries());
    const decodedObj: { [key: string]: any } = {};

    for (const key in params) {
        if (params.hasOwnProperty(key)) {
            const decodedValue = typeof params[key] === 'string' ? decodeURIComponent(params[key]) : params[key];
            decodedObj[key] = decodedValue;
        }
    }

    const { QuoteID, Revision, ItemID, ...rest } = decodedObj

    return (
        <>
            <Backdrop open={isLoading}>
                <CircularProgress color="primary" />
            </Backdrop>

            <ContentHeader title={'Quotes'} />
            {!isLoading && (
                <Formik
                    initialValues={{
                        ClientID: '',
                        SalesRepID: '',
                        Project: '',
                        ProductAreaID: '',
                        QuoteStatusID: '',
                        EnquirySourceID: '',
                        FanSerial: '',
                        'Created%2eMin': '',
                        'Created%2eMax': '',
                        'Awarded%2eMin': '',
                        'Awarded%2eMax': '',
                        'Attributes%2eExportTimestamp%2eMin': '',
                        'Attributes%2eExportTimestamp%2eMax': '',
                        QuoteStateID: 1,
                        ...rest,
                        QuoteNo: [QuoteID, Revision, ItemID].filter(Boolean).join('') || '',
                        ...(rest['Created%2eMin'] && { 'Created%2eMin': new Date(rest['Created%2eMin']).toISOString().split('T')[0]}),
                        ...(rest['Created%2eMax'] && { 'Created%2eMax': new Date(rest['Created%2eMax']).toISOString().split('T')[0]}),
                        ...(rest['Awarded%2eMin'] && { 'Awarded%2eMin': new Date(rest['Awarded%2eMin']).toISOString().split('T')[0]}),
                        ...(rest['Awarded%2eMax'] && { 'Awarded%2eMax': new Date(rest['Awarded%2eMax']).toISOString().split('T')[0]}),
                        ...(rest['Attributes%2eexportTimestamp%2eMin'] && { 'Attributes%2eexportTimestamp%2eMin': new Date(rest['Attributes%2eexportTimestamp%2eMin']).toISOString().split('T')[0]}),
                        ...(rest['Attributes%2eexportTimestamp%2eMax'] && { 'Attributes%2eexportTimestamp%2eMax': new Date(rest['Attributes%2eexportTimestamp%2eMax']).toISOString().split('T')[0]}),
                    }}
                    onSubmit={async (values) => {
                        setFilterValues(createFilterValues(values, history, searchParams));
                    }}
                >
                    {(formProps) => {
                        const { handleSubmit, isSubmitting, setFieldValue, values } = formProps;

                        return (
                            <>
                                <ContentWrapper>
                                    <form onSubmit={handleSubmit}>
                                        <Input label="Quote No" name="QuoteNo" type="text" style={{ width: '10%', marginRight: '1%' }} />

                                        <SelectWithData
                                            reset={qSReset}
                                            setFieldValue={setFieldValue}
                                            label="Quote Status"
                                            name="QuoteStatusID"
                                            defaultValue={quoteStatusList.find((v:any) => v.id === parseInt(decodedObj.QuoteStatusID))}
                                            options={quoteStatusList}
                                            style={{ width: '10%', marginRight: '1%' }}
                                        />

                                        <SelectWithData
                                            reset={cReset}
                                            setFieldValue={setFieldValue}
                                            label="Client"
                                            name="ClientID"
                                            defaultValue={clientList.find((v:any) => v.id === parseInt(decodedObj.ClientID))}
                                            options={clientList}
                                            style={{ width: '14%', marginRight: '1%' }}
                                        />

                                        <SelectWithData
                                            reset={sReset}
                                            setFieldValue={setFieldValue}
                                            label="Sales Rep"
                                            name="SalesRepID"
                                            defaultValue={salesRepList.find((v:any) => v.id === decodedObj.SalesRepID)}
                                            options={salesRepList}
                                            style={{ width: '14%', marginRight: '1%' }}
                                        />

                                        <SelectWithData
                                            reset={sBReset}
                                            setFieldValue={setFieldValue}
                                            label="Selected By"
                                            name="SelectedByID"
                                            defaultValue={salesRepList.find((v:any) => v.id === decodedObj.SelectedByID)}
                                            options={salesRepList}
                                            style={{ width: '14%', marginRight: '1%' }}
                                        />


                                        <SelectWithData
                                            reset={paReset}
                                            setFieldValue={setFieldValue}
                                            label="Product Area"
                                            name="ProductAreaID"
                                            defaultValue={productAreaList.find((v:any) => v.id === parseInt(decodedObj.ProductAreaID))}
                                            options={productAreaList}
                                            style={{ width: '14%', marginRight: '1%' }}
                                        />

                                        <SelectWithData
                                            reset={esReset}
                                            setFieldValue={setFieldValue}
                                            label="Enquiry Source"
                                            name="EnquirySourceID"
                                            defaultValue={enquirySourceList.find((v:any) => v.id === parseInt(decodedObj.EnquirySourceID))}
                                            options={enquirySourceList}
                                            style={{ width: '14%', marginRight: '1%' }}
                                        />

                                        <Input label="Project" name="Project" type="text" style={{ width: '15%',  marginRight: '1%' }} />

                                        <Input
                                            label="Fan Design"
                                            name="Attributes%2eFanDesign"
                                            type="text"
                                            style={{ width: '16%', marginRight: '1%' }}
                                        />

                                        <Input label="Fan Size" name="Attributes%2eFanSize" type="text" style={{ width: '14%', marginRight: '1%' }} />
                                        <Input
                                            label="Fan Serial No"
                                            name="FanSerial"
                                            type="text"
                                            style={{ width: '14%', marginRight: '1%' }}
                                        />
                                        <Input
                                            label="Aura Job File No"
                                            name="Attributes%2eAuraJobFileNo"
                                            type="text"
                                            style={{ width: '14%', marginRight: '1%' }}
                                        />
                                        <Input
                                            label="NYB Sales Memo No"
                                            name="Attributes%2eNYBSalesMemoNo"
                                            type="text"
                                            style={{ width: '14%', marginRight: '1%' }}
                                        />

                                        <SelectWithData
                                            reset={iReset}
                                            setFieldValue={setFieldValue}
                                            label="Industry"
                                            name="industryID"
                                            defaultValue={industryList.find((v:any) => v.id === parseInt(decodedObj.industryID))}
                                            options={industryList}
                                            style={{ width: '14%', marginRight: '1%' }}
                                        />

                                        <SelectWithData
                                            reset={aReset}
                                            setFieldValue={setFieldValue}
                                            label="Application"
                                            name="applicationID"
                                            defaultValue={applicationList.find((v:any) => v.id === parseInt(decodedObj.applicationID))}
                                            options={applicationList}
                                            style={{ width: '14%', marginRight: '1%' }}
                                        />

                                        <Input
                                            label="Quote Date Start"
                                            name="Created%2eMin"
                                            type="date"
                                            style={{ width: '14%', marginRight: '1%' }}
                                        />

                                        <Input
                                            label="Quote Date End"
                                            name="Created%2eMax"
                                            type="date"
                                            style={{ width: '14%', marginRight: '1%' }}
                                        />

                                        <Input
                                            label="Award Date Start"
                                            name="Awarded%2eMin"
                                            type="date"
                                            style={{ width: '14%', marginRight: '1%' }}
                                        />

                                        <Input label="Award Date End" name="Awarded%2eMax" type="date" style={{ width: '14%', marginRight: '1%' }} />

                                        { userAttr.roles.includes("Admin") && 
                                            <SelectWithData reset={qStateReset} setFieldValue={setFieldValue} defaultValue={quoteStateList.find((v:any) => v.id === parseInt(decodedObj.QuoteStateID)) || {label: 'Open', id : 1}} label='Quote State' name='QuoteStateID' options={quoteStateList} style={{ width: '14%', marginRight: '1%' }} />
                                        }

                                        <SelectWithData reset={probStateReset} setFieldValue={setFieldValue} label='Probability' name='probability' defaultValue={probabiltyList.find((v:any) => v.id === decodedObj.probability)} options={probabiltyList} style={{ width: '14%', marginRight: '1%' }} />

                                        <Input
                                            label="Export Date Start"
                                            name="Attributes%2eExportTimestamp%2eMin"
                                            type="date"
                                            style={{ width: '14%', marginRight: '1%' }}
                                        />

                                        <Input
                                            label="Export Date End"
                                            name="Attributes%2eExportTimestamp%2eMax"
                                            type="date"
                                            style={{ width: '14%', marginRight: '1%' }}
                                        />
                                        
                                        <Input
                                            label="Fan Group Number"
                                            name="Attributes%2eFanGroupNumber"
                                            type="text"
                                            style={{ width: '14%', marginRight: '1%' }}
                                        />
                                        
                                        <Grid container justify="flex-end">
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                type="button"
                                                onClick={() => {
                                                    setFieldValue('QuoteNo', '');
                                                    setFieldValue('ClientID', '');
                                                    setCReset(`c${Date.now()}`);
                                                    setFieldValue('SalesRepID', '');
                                                    setSReset(`s${Date.now()}`);
                                                    setSBReset(`sB${Date.now()}`);
                                                    setFieldValue('Project', '');
                                                    setFieldValue('ProductAreaID', '');
                                                    setPaReset(`pa${Date.now()}`);
                                                    setFieldValue('QuoteStatusID', '');
                                                    setQsReset(`qs${Date.now()}`);
                                                    setFieldValue('EnquirySourceID', '');
                                                    setEsReset(`es${Date.now()}`);
                                                    setFieldValue('Attributes%2eFanDesign', '');
                                                    setFieldValue('Attributes%2eFanSize', '');
                                                    setFieldValue('FanSerial', '');
                                                    setFieldValue('Attributes%2eAuraJobFileNo', '');
                                                    setFieldValue('Attributes%2eNYBSalesMemoNo', '');
                                                    setIReset(`i${Date.now()}`);
                                                    setAReset(`a${Date.now()}`);
                                                    setFieldValue('Created%2eMin', '');
                                                    setFieldValue('Created%2eMax', '');
                                                    setFieldValue('Awarded%2eMin', '');
                                                    setFieldValue('Awarded%2eMax', '');
                                                    setQStateReset(`qState${Date.now()}`);
                                                    setProbStateReset(`prob${Date.now()}`);
                                                    setFieldValue('Attributes%2eExportTimestamp%2eMin', '');
                                                    setFieldValue('Attributes%2eExportTimestamp%2eMax', '');
                                                    setFieldValue('Attributes%2eFanGroupNumber', '');
                                                    
                                                    setFilterValues(createFilterValues({
                                                        QuoteNo: '',
                                                        ClientID: '',
                                                        SalesRepID: '',
                                                        Project: '',
                                                        ProductAreaID: '',
                                                        QuoteStatusID: '',
                                                        EnquirySourceID: '',
                                                        FanSerial: '',
                                                        'Created%2eMin': '',
                                                        'Created%2eMax': '',
                                                        'Awarded%2eMin': '',
                                                        'Awarded%2eMax': '',
                                                        'Attributes%2eExportTimestamp%2eMin': '',
                                                        'Attributes%2eExportTimestamp%2eMax': '',
                                                        FanGroupNumber: '',
                                                        QuoteStateID: 1,
                                                    }, history, new URLSearchParams()));
                                                }}
                                            >
                                                Reset Filters
                                            </Button>
                                            &nbsp;
                                            <Button variant="contained" color="secondary" type="submit" disabled={isSubmitting}>
                                                Filter Quotes
                                            </Button>
                                        </Grid>
                                    </form>
                                </ContentWrapper>

                                <BrowseData minWidth="1500px" />

                                <Grid container justify="flex-end">
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={async () => {
                                            const url = browseConfig.createApiPath(
                                                browseConfig.apiBasePath,
                                                1,
                                                10000000,
                                                setFilterValues(createFilterValues(values, history, searchParams))
                                            );
                                            const res = await axiosInstance({ url });

                                            if (res.data.length > 0) {
                                                const csvHeaders = [
                                                    'Quote No',
                                                    'Quote Date',
                                                    'Award Date',
                                                    'Client Name',
                                                    'Client State',
                                                    'Sales Rep',
                                                    'Product Area',
                                                    'Combined Price Sell',
                                                    'Combined Price Cost',
                                                ];

                                                const csvData = res.data
                                                    .map(
                                                        ({
                                                            quoteID,
                                                            revision,
                                                            itemID,
                                                            createdAt,
                                                            awardedAt,
                                                            clientDetails,
                                                            salesRepName,
                                                            productAreaName,
                                                            combinedPrice,
                                                            combinedCost,
                                                        }: any) => {
                                                            const { name, state } =
                                                                clientDetails != null
                                                                    ? {
                                                                          name: clientDetails.name,
                                                                          state: clientDetails.addressDetails.state,
                                                                      }
                                                                    : { name: 'NA', state: 'NA' };
                                                            return `${quoteID}${revision}${itemID},\
                                                    ${createdAt.slice(8, 10)}/${createdAt.slice(5, 7)}/${createdAt.slice(0, 4)},\
                                                    ${awardedAt.slice(8, 10)}/${awardedAt.slice(5, 7)}/${awardedAt.slice(
                                                                0,
                                                                4,
                                                            )},${name},${state},${
                                                                salesRepName != null ? salesRepName : ''
                                                            },${productAreaName},${combinedPrice},${combinedCost}`;
                                                        },
                                                    )
                                                    .join('\n');

                                                const blob = new Blob([`${csvHeaders.join(',')} \n` + csvData], {
                                                    type: 'text/plain;charset=utf-8',
                                                });
                                                saveAs(blob, `report.csv`);
                                            } else {
                                                dispatch(addFeedback('No results found matching filter to export', 'error'));
                                            }
                                        }}
                                    >
                                        Export to .csv
                                    </Button>
                                </Grid>
                            </>
                        );
                    }}
                </Formik>
            )}
        </>
    );
};

const WrappedApp = (props: any) => (
    <BrowseProvider config={browseConfig} pageSize={15}>
        <Quotes {...props} />
    </BrowseProvider>
);

export default WrappedApp;
